import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

declare const __SENTRY_RELEASE__: string | undefined;

/** Injected at build time by vite.config.ts; matches the uploaded source maps. */
const RELEASE = typeof __SENTRY_RELEASE__ !== "undefined" ? __SENTRY_RELEASE__ : undefined;

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

let initialized = false;

/** True once Sentry.init() has run with a valid DSN. */
export const isSentryReady = () => initialized;

/**
 * Fetches the public DSN from the backend and boots Sentry.
 * Non-blocking: the app renders immediately and Sentry attaches shortly after.
 */
export async function initSentry(): Promise<void> {
  if (initialized || !SUPABASE_URL) return;
  // Dev/preview HMR restarts generate noise (failed module scripts, vite
  // connection drops) that isn't actionable. Only report real deployments.
  if (import.meta.env.DEV) return;


  let dsn: string | null = null;
  let environment = import.meta.env.DEV ? "development" : "production";

  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/sentry-config`, {
      headers: PUBLISHABLE_KEY ? { apikey: PUBLISHABLE_KEY } : undefined,
    });
    if (!res.ok) return;
    const cfg = (await res.json()) as { dsn?: string | null; environment?: string };
    dsn = cfg.dsn ?? null;
    if (cfg.environment && !import.meta.env.DEV) environment = cfg.environment;
  } catch {
    return; // Never let monitoring setup break the app.
  }

  if (!dsn) return;

  Sentry.init({
    dsn,
    environment,
    release: RELEASE,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Sample lightly in production to stay inside the free tier.
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications.",
      "Non-Error promise rejection captured",
      // Transient chunk-fetch failures (deploys, flaky networks) — we already
      // retry and reload automatically in lazy-retry.ts.
      "Importing a module script failed",
      "Failed to fetch dynamically imported module",
      "error loading dynamically imported module",
      "Loading chunk",
    ],

  });

  initialized = true;
}

/** Attaches the signed-in user to subsequent Sentry events. */
export function setSentryUser(user: { id: string; email?: string | null } | null) {
  if (!initialized) return;
  Sentry.setUser(user ? { id: user.id, email: user.email ?? undefined } : null);
}

export { Sentry };
